/**
 * Self-Cert Individual Models
 * Type-safe models for Self-Cert Individual form data handling, validation, and XML export
 */

import { generateSelfCertIndividualXML } from "../components/SendingForms/utils/generateSelfCertIndividualXML";

// API Response Model - Complete form fields from API
export class SelfCertIndividualResponseModel {
  // Dynamic fields support
  [key: string]: any;

  // Core Form Fields
  "Form_name": string = "Self-Cert Individual";
  "UniqueIdentifier": string = "";

  // Individual Information
  "SC_Individual_Title": string = "";
  "SC_Individual_Last_name": string = "";
  "SC_Individual_First_name": string = "";
  "SC_Individual_Middle_name": string = "";
  "SC_Individual_City_of_birth": string = "";
  "SC_Individual_Country_of_birth": string = "";
  "SC_Individual_Date_of_birth": string = "";

  // Current Residence Address
  "SC_Individual_Current_residence_address_street": string = "";
  "SC_Individual_Current_residence_address_city": string = "";
  "SC_Individual_Current_residence_address_state": string = "";
  "SC_Individual_Current_residence_address_country": string = "";
  "SC_Individual_Current_residence_address_postal": string = "";

  // Mailing Address
  "SC_Individual_Mailing_address_street": string = "";
  "SC_Individual_Mailing_address_city": string = "";
  "SC_Individual_Mailing_address_state": string = "";
  "SC_Individual_Mailing_address_country": string = "";
  "SC_Individual_Mailing_address_postal": string = "";

  // Tax Information
  "SC_Individual_Country_tax_residence": string = "";
  "SC_Individual_TIN": string = "";
  "SC_Individual_TIN_type": string = "";
  "SC_Individual_TIN_not_available": boolean = false;
  "SC_Individual_TIN_not_available_reason": string = "";
  "SC_Individual_Sole_residency_yes": boolean = false;
  "SC_Individual_Sole_residency_no": boolean = false;

  // Additional Tax Countries (up to 3)
  "SC_Individual_Additional_tax_country_1": string = "";
  "SC_Individual_Additional_TIN_1": string = "";
  "SC_Individual_Additional_TIN_type_1": string = "";
  "SC_Individual_Additional_tax_country_2": string = "";
  "SC_Individual_Additional_TIN_2": string = "";
  "SC_Individual_Additional_TIN_type_2": string = "";
  "SC_Individual_Additional_tax_country_3": string = "";
  "SC_Individual_Additional_TIN_3": string = "";
  "SC_Individual_Additional_TIN_type_3": string = "";

  // Signature Information
  "SC_Individual_Signature": string = "";
  "SC_Individual_Print_name": string = "";
  "SC_Individual_Capacity": string = "";
  "SC_Individual_Date": string = "";
  "SC_Individual_Signature_unreadable": boolean = false;
  "SC_Individual_isSigned": string = "no";
  "SC_Individual_isModified": string = "no";
  "certify": boolean = false;
  "isSigned": string = "no";
  "isModified": string = "no";

  constructor(data?: any) {
    if (data) {
      Object.assign(this, data);
    }
  }
}

export class SelfCertIndividualFormModel {
  // Dynamic fields support
  [key: string]: any;

  // Core Form Fields
  "Form_name": string = "Self-Cert Individual";
  "UniqueIdentifier": string = "";

  // Individual Information
  "SC_Individual_Title": string = "";
  "SC_Individual_Last_name": string = "";
  "SC_Individual_First_name": string = "";
  "SC_Individual_Middle_name": string = "";
  "SC_Individual_City_of_birth": string = "";
  "SC_Individual_Country_of_birth": string = "";
  "SC_Individual_Date_of_birth": string = "";

  // Current Residence Address
  "SC_Individual_Current_residence_address_street": string = "";
  "SC_Individual_Current_residence_address_city": string = "";
  "SC_Individual_Current_residence_address_state": string = "";
  "SC_Individual_Current_residence_address_country": string = "";
  "SC_Individual_Current_residence_address_postal": string = "";

  // Mailing Address
  "SC_Individual_Mailing_address_street": string = "";
  "SC_Individual_Mailing_address_city": string = "";
  "SC_Individual_Mailing_address_state": string = "";
  "SC_Individual_Mailing_address_country": string = "";
  "SC_Individual_Mailing_address_postal": string = "";

  // Tax Information
  "SC_Individual_Country_tax_residence": string = "";
  "SC_Individual_TIN": string = "";
  "SC_Individual_TIN_type": string = "";
  "SC_Individual_TIN_not_available": boolean = false;
  "SC_Individual_TIN_not_available_reason": string = "";
  "SC_Individual_Sole_residency_yes": boolean = false;
  "SC_Individual_Sole_residency_no": boolean = false;

  // Additional Tax Countries (up to 3)
  "SC_Individual_Additional_tax_country_1": string = "";
  "SC_Individual_Additional_TIN_1": string = "";
  "SC_Individual_Additional_TIN_type_1": string = "";
  "SC_Individual_Additional_tax_country_2": string = "";
  "SC_Individual_Additional_TIN_2": string = "";
  "SC_Individual_Additional_TIN_type_2": string = "";
  "SC_Individual_Additional_tax_country_3": string = "";
  "SC_Individual_Additional_TIN_3": string = "";
  "SC_Individual_Additional_TIN_type_3": string = "";

  // Signature Information
  "SC_Individual_Signature": string = "";
  "SC_Individual_Print_name": string = "";
  "SC_Individual_Capacity": string = "";
  "SC_Individual_Date": string = "";
  "SC_Individual_Signature_unreadable": boolean = false;
  "SC_Individual_isSigned": string = "no";
  "SC_Individual_isModified": string = "no";
  "certify": boolean = false;
  "isSigned": string = "no";
  "isModified": string = "no";

  // Additional jurisdictions fields (for table data mapping)
  "sc_cp Jurisdiction 1": string = "";
  "sc_cp Tax reference number type": string = "";
  "sc_cp Tax Identification Number": string = "";
  "sc_cp Unable to procure TIN": boolean = false;
  "sc_cp Reason for non-availability of TIN": string = "";
  // Sole residency UI fields
  "sc_cp Sole Residency Yes": boolean = false;
  "sc_cp Sole Residency No": boolean = false;
  // Additional jurisdictions 2-4
  "sc_cp Jurisdiction 2": string = "";
  "sc_cp Tax Identification Number 2": string = "";
  "sc_cp Tax Reference Number Type 2": string = "";
  "sc_cp Reason for non-availability of TIN 2": string = "";
  "sc_cp Jurisdiction 3": string = "";
  "sc_cp Tax Identification Number 3": string = "";
  "sc_cp Tax Reference Number Type 3": string = "";
  "sc_cp Reason for non-availability of TIN 3": string = "";
  "sc_cp Jurisdiction 4": string = "";
  "sc_cp Tax Identification Number 4": string = "";
  "sc_cp Tax Reference Number Type 4": string = "";
  "sc_cp Reason for non-availability of TIN 4": string = "";

  // Mapping control flag
  "isMappedDone": boolean = false;

  constructor(data?: Partial<SelfCertIndividualFormModel>) {
    if (data) {
      Object.assign(this, data);
    }
  }

  /**
   * Generate XML representation using the Self-Cert Individual XML format
   */
  async toXML(): Promise<string> {
    // Map model data to the format expected by XML generator
    const xmlData = {
      // Map form fields to XML field names
      BusinessUnit: "", // Will need to be provided or mapped
      TitleName: this["SC_Individual_Title"],
      firstName: this["SC_Individual_First_name"],
      lastName: this["SC_Individual_Last_name"],
      MiddleName: this["SC_Individual_Middle_name"],
      CountryOfBirth: this["SC_Individual_Country_of_birth"],
      DateOfBirth: this["SC_Individual_Date_of_birth"],
      CityTownOfBirth: this["SC_Individual_City_of_birth"],

      // Permanent Address (using current residence address)
      PermenantAddress_Line1: this["SC_Individual_Current_residence_address_street"],
      PermenantAddress_City: this["SC_Individual_Current_residence_address_city"],
      PermenantAddress_State: this["SC_Individual_Current_residence_address_state"],
      PermenantAddress_Country: this["SC_Individual_Current_residence_address_country"],
      PermenantAddress_Zip: this["SC_Individual_Current_residence_address_postal"],

      // Mailing Address
      MailingAddress_Line1: this["SC_Individual_Mailing_address_street"],
      MailingAddress_City: this["SC_Individual_Mailing_address_city"],
      MailingAddress_State: this["SC_Individual_Mailing_address_state"],
      MailingAddress_Country: this["SC_Individual_Mailing_address_country"],
      MailingAddress_Zip: this["SC_Individual_Mailing_address_postal"],

      // Tax Information
      TaxResidencyCountry: this["SC_Individual_Country_tax_residence"],
      TaxResidencyTIN: this["SC_Individual_TIN"],
      TaxResidencyTINType: this["SC_Individual_TIN_type"],
      cbTaxResidencyTINNotAvailable: this["SC_Individual_TIN_not_available"],
      TaxResidencyTINNotAvailableReason: this["SC_Individual_TIN_not_available_reason"],

      // Additional Tax Countries
      taxCountry1: this["SC_Individual_Additional_tax_country_1"],
      taxTIN1: this["SC_Individual_Additional_TIN_1"],
      taxCountry1IDType: this["SC_Individual_Additional_TIN_type_1"],
      taxCountry2: this["SC_Individual_Additional_tax_country_2"],
      taxTIN2: this["SC_Individual_Additional_TIN_2"],
      taxCountry2IDType: this["SC_Individual_Additional_TIN_type_2"],
      taxCountry3: this["SC_Individual_Additional_tax_country_3"],
      taxTIN3: this["SC_Individual_Additional_TIN_3"],
      taxCountry3IDType: this["SC_Individual_Additional_TIN_type_3"],

      // Signature Information
      Signature_TextBox: this["isSigned"] === "yes" || this["isSigned"] === "true",
      SignedBy: this["SC_Individual_Print_name"],
      Capacity: this["SC_Individual_Capacity"],
      SignDate: this["SC_Individual_Date"],
      Form_OCR_Signature: this["SC_Individual_Signature"],

      // Include all other fields from the model
      ...this,
    };

    return await generateSelfCertIndividualXML(xmlData);
  }

  /**
   * Convert to API payload format
   */
  toApiPayload(): Record<string, any> {
    return {
      ...this,
    };
  }

  /**
   * Validate the form data
   */
  validate(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Required field validations
    if (!this["SC_Individual_First_name"]) {
      errors.push("First name is required");
    }
    if (!this["SC_Individual_Last_name"]) {
      errors.push("Last name is required");
    }
    if (!this["SC_Individual_Country_tax_residence"]) {
      errors.push("Country of tax residence is required");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

/**
 * Self-Cert Individual Field Mapper - Maps response model fields to form model fields
 */
export class SelfCertIndividualFieldMapper {
  /**
   * Map response model to form model
   */
  static mapResponseToForm(
    responseModel: SelfCertIndividualResponseModel
  ): SelfCertIndividualFormModel {
    const formModel = new SelfCertIndividualFormModel();

    // Check if mapping is already done
    if (responseModel["isMappedDone"]) {
      // If already mapped, just return the existing data as form model
      Object.assign(formModel, responseModel);
      return formModel;
    }

    console.log(
      "🔍 SelfCertIndividual: First time mapping - isMappedDone not set, proceeding with table mapping"
    );

    // Direct field mappings
    const directMappings = [
      "UniqueIdentifier",
      "Form_name",
      "SC_Individual_Title",
      "SC_Individual_Last_name",
      "SC_Individual_First_name",
      "SC_Individual_Middle_name",
      "SC_Individual_City_of_birth",
      "SC_Individual_Country_of_birth",
      "SC_Individual_Date_of_birth",
      "SC_Individual_Current_residence_address_street",
      "SC_Individual_Current_residence_address_city",
      "SC_Individual_Current_residence_address_state",
      "SC_Individual_Current_residence_address_country",
      "SC_Individual_Current_residence_address_postal",
      "SC_Individual_Mailing_address_street",
      "SC_Individual_Mailing_address_city",
      "SC_Individual_Mailing_address_state",
      "SC_Individual_Mailing_address_country",
      "SC_Individual_Mailing_address_postal",
      "SC_Individual_Country_tax_residence",
      "SC_Individual_Sole_residency_yes",
      "SC_Individual_Sole_residency_no",
      "SC_Individual_TIN",
      "SC_Individual_TIN_type",
      "SC_Individual_TIN_not_available",
      "SC_Individual_TIN_not_available_reason",
      "SC_Individual_Additional_tax_country_1",
      "SC_Individual_Additional_TIN_1",
      "SC_Individual_Additional_TIN_type_1",
      "SC_Individual_Additional_tax_country_2",
      "SC_Individual_Additional_TIN_2",
      "SC_Individual_Additional_TIN_type_2",
      "SC_Individual_Additional_tax_country_3",
      "SC_Individual_Additional_TIN_3",
      "SC_Individual_Additional_TIN_type_3",
      "SC_Individual_Signature",
      "SC_Individual_Print_name",
      "SC_Individual_Capacity",
      "SC_Individual_Date",
      "SC_Individual_Signature_unreadable",
      "SC_Individual_isSigned",
      "SC_Individual_isModified",
      "certify",
      "isSigned",
      "isModified",
    ];

    directMappings.forEach((field) => {
      if (responseModel[field] !== undefined) {
        formModel[field] = responseModel[field];
      }
    });

    // Special mappings
    this.mapSpecialFields(responseModel, formModel);

    // Set isMappedDone to true to prevent re-mapping
    formModel.isMappedDone = true;

    return formModel;
  }

  /**
   * Handle special field mappings and transformations
   */
  private static mapSpecialFields(
    responseModel: SelfCertIndividualResponseModel,
    formModel: SelfCertIndividualFormModel
  ): void {
    // Map API fields (hyphen format) to form fields (underscore format)
    const fieldMappings = {
      "SC-Individual-Title": "SC_Individual_Title",
      "SC-Individual-Last_name": "SC_Individual_Last_name",
      "SC-Individual-First_name": "SC_Individual_First_name",
      "SC-Individual-Middle_name": "SC_Individual_Middle_name",
      "SC-Individual-City_of_birth": "SC_Individual_City_of_birth",
      "SC-Individual-Country_of_birth": "SC_Individual_Country_of_birth",
      "SC-Individual-Date_of_birth": "SC_Individual_Date_of_birth",
      "SC-Individual-Current_residence_address_street":
        "SC_Individual_Current_residence_address_street",
      "SC-Individual-Current_residence_address_city":
        "SC_Individual_Current_residence_address_city",
      "SC-Individual-Current_residence_address_state":
        "SC_Individual_Current_residence_address_state",
      "SC-Individual-Current_residence_address_country":
        "SC_Individual_Current_residence_address_country",
      "SC-Individual-Current_residence_address_postal":
        "SC_Individual_Current_residence_address_postal",
      "SC-Individual-Mailing_address_street": "SC_Individual_Mailing_address_street",
      "SC-Individual-Mailing_address_city": "SC_Individual_Mailing_address_city",
      "SC-Individual-Mailing_address_state": "SC_Individual_Mailing_address_state",
      "SC-Individual-Mailing_address_country": "SC_Individual_Mailing_address_country",
      "SC-Individual-Mailing_address_postal": "SC_Individual_Mailing_address_postal",
      "SC-Individual-Tax_residency_country": "SC_Individual_Country_tax_residence",
      "SC-Individual-Tax_reference_number_type": "SC_Individual_TIN_type",
      "SC-Individual-Tax_id": "SC_Individual_TIN",
      "SC-Individual-Sole_residency_yes": "SC_Individual_Sole_residency_yes",
      "SC-Individual-Sole_residency_no": "SC_Individual_Sole_residency_no",
      "SC-Individual-Tin_or_functional_equivalent": "SC_Individual_TIN_not_available",
      "SC-Individual-Reason_for_non_tin": "SC_Individual_TIN_not_available_reason",
      "SC-Individual-Signature": "SC_Individual_Signature",
      "SC-Individual-Signer_printed_name": "SC_Individual_Print_name",
      "SC-Individual-Sign_date": "SC_Individual_Date",
      "SC-Individual-Capacity": "SC_Individual_Capacity",
    };

    Object.entries(fieldMappings).forEach(([apiField, formField]) => {
      if (responseModel[apiField] !== undefined) {
        // Convert "selected" strings to boolean true for checkbox fields
        if (responseModel[apiField] === "selected") {
          formModel[formField] = true;
          console.log(
            `SelfCertIndividual mapping: ${apiField} → ${formField} = true (converted from "selected")`
          );
        } else {
          formModel[formField] = responseModel[apiField];
          console.log(
            `SelfCertIndividual mapping: ${apiField} → ${formField} = ${responseModel[apiField]}`
          );
        }
      }
    });

    // Mirror primary Section II values into the UI field names used by the form
    // so that Jurisdiction 1 and related inputs display the mapped values.
    if (formModel["SC_Individual_Country_tax_residence"] !== undefined) {
      formModel["sc_cp Jurisdiction 1"] = formModel["SC_Individual_Country_tax_residence"];
    }
    if (formModel["SC_Individual_TIN_type"] !== undefined) {
      formModel["sc_cp Tax reference number type"] = formModel["SC_Individual_TIN_type"];
    }
    if (formModel["SC_Individual_TIN"] !== undefined) {
      formModel["sc_cp Tax Identification Number"] = formModel["SC_Individual_TIN"];
    }
    if (formModel["SC_Individual_TIN_not_available"] !== undefined) {
      formModel["sc_cp Unable to procure TIN"] = !!formModel["SC_Individual_TIN_not_available"];
    }
    if (formModel["SC_Individual_TIN_not_available_reason"] !== undefined) {
      formModel["sc_cp Reason for non-availability of TIN"] =
        formModel["SC_Individual_TIN_not_available_reason"];
    }

    // Mirror sole residency values to UI field names used by the form
    if (formModel["SC_Individual_Sole_residency_yes"] !== undefined) {
      formModel["sc_cp Sole Residency Yes"] = !!formModel["SC_Individual_Sole_residency_yes"];
    }
    if (formModel["SC_Individual_Sole_residency_no"] !== undefined) {
      formModel["sc_cp Sole Residency No"] = !!formModel["SC_Individual_Sole_residency_no"];
    }

    let tableData = null;

    // Check for table data in different locations - prioritize new tax_purposes structure
    if (responseModel["tables"] && responseModel["tables"]["tax_purposes"]) {
      tableData = responseModel["tables"]["tax_purposes"];
      console.log("✅ SelfCertIndividual table data from tables.tax_purposes:", tableData);
    } else {
      console.log("❌ SelfCertIndividual: No table data found in responseModel");
      console.log("🔍 SelfCertIndividual: Full responseModel structure:", responseModel);
    }

    // Helper: normalize a string (lowercase, remove non-alphanumerics)
    const normalize = (s: string) =>
      (s || "")
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "");
    // Helper: find value in a row by matching any of the candidate headers loosely
    const getByHeader = (row: any, candidates: string[]): any => {
      const keys = Object.keys(row || {});
      for (const k of keys) {
        const nk = normalize(k);
        for (const cand of candidates) {
          if (nk.includes(normalize(cand))) {
            return row[k];
          }
        }
      }
      return undefined;
    };

    if (tableData && Array.isArray(tableData)) {
      console.log(`✅ SelfCertIndividual: Processing ${tableData.length} table rows`);
      tableData.forEach((row: any, index: number) => {
        const jurisdictionNum = index + 2; // Start from 2 (Jurisdiction 2, 3, 4)
        console.log(
          `🔍 SelfCertIndividual: Processing row ${index} for jurisdiction ${jurisdictionNum}:`,
          row
        );

        // New direct mapping for tax_purposes structure
        if (row.tax_residence !== undefined) {
          formModel[`sc_cp Jurisdiction ${jurisdictionNum}`] = row.tax_residence;
          console.log(
            `✅ SelfCertIndividual table mapping: Jurisdiction ${jurisdictionNum} = ${row.tax_residence}`
          );
        }

        if (row.tax_id !== undefined) {
          formModel[`sc_cp Tax Identification Number ${jurisdictionNum}`] = row.tax_id;
          console.log(
            `✅ SelfCertIndividual table mapping: Tax ID ${jurisdictionNum} = ${row.tax_id}`
          );
        }

        if (row.tax_reference_number_type !== undefined) {
          formModel[`sc_cp Tax Reference Number Type ${jurisdictionNum}`] =
            row.tax_reference_number_type;
          console.log(
            `✅ SelfCertIndividual table mapping: Tax Ref Type ${jurisdictionNum} = ${row.tax_reference_number_type}`
          );
        }

        if (row.reason_for_non_tin !== undefined) {
          formModel[`sc_cp Reason for non-availability of TIN ${jurisdictionNum}`] =
            row.reason_for_non_tin;
          console.log(
            `✅ SelfCertIndividual table mapping: Reason ${jurisdictionNum} = ${row.reason_for_non_tin}`
          );
        }

        // Fallback to old table mapping logic for backward compatibility
        const jurVal = getByHeader(row, ["Jurisdiction of Tax Residence", "Jurisdiction"]);
        if (
          jurVal !== undefined &&
          formModel[`sc_cp Jurisdiction ${jurisdictionNum}`] === undefined
        ) {
          formModel[`sc_cp Jurisdiction ${jurisdictionNum}`] = jurVal;
          console.log(
            `✅ SelfCertIndividual table mapping (fallback): Jurisdiction ${jurisdictionNum} = ${jurVal}`
          );
        }

        const tinVal = getByHeader(row, ["Tax Identification Number", "Tax ID"]);
        if (
          tinVal !== undefined &&
          formModel[`sc_cp Tax Identification Number ${jurisdictionNum}`] === undefined
        ) {
          formModel[`sc_cp Tax Identification Number ${jurisdictionNum}`] = tinVal;
          console.log(
            `✅ SelfCertIndividual table mapping (fallback): Tax ID ${jurisdictionNum} = ${tinVal}`
          );
        }

        const refTypeVal = getByHeader(row, ["Tax Reference Number Type", "Reference Number Type"]);
        if (
          refTypeVal !== undefined &&
          formModel[`sc_cp Tax Reference Number Type ${jurisdictionNum}`] === undefined
        ) {
          formModel[`sc_cp Tax Reference Number Type ${jurisdictionNum}`] = refTypeVal;
          console.log(
            `✅ SelfCertIndividual table mapping (fallback): Tax Ref Type ${jurisdictionNum} = ${refTypeVal}`
          );
        }

        const reasonVal = getByHeader(row, [
          "Reason for non-availability of TIN (if applicable)",
          "Reason for non-availability",
        ]);
        if (
          reasonVal !== undefined &&
          formModel[`sc_cp Reason for non-availability of TIN ${jurisdictionNum}`] === undefined
        ) {
          formModel[`sc_cp Reason for non-availability of TIN ${jurisdictionNum}`] = reasonVal;
          console.log(
            `✅ SelfCertIndividual table mapping (fallback): Reason ${jurisdictionNum} = ${reasonVal}`
          );
        }
      });
      console.log("✅ SelfCertIndividual: Table data mapping completed");
    } else {
      console.log("❌ SelfCertIndividual: No valid table data to process");
    }

    // Auto-set isSigned based on Signature field
    if (
      responseModel["SC-Individual-Signature"] &&
      responseModel["SC-Individual-Signature"].toString().trim()
    ) {
      formModel["isSigned"] = "yes";
    } else {
      formModel["isSigned"] = "no";
    }

    // Ensure radios default to 'no' if not provided
    if (!formModel["SC_Individual_isSigned"]) {
      formModel["SC_Individual_isSigned"] = "no";
    }
    if (!formModel["SC_Individual_isModified"]) {
      formModel["SC_Individual_isModified"] = "no";
    }

    // Map additional response fields to form fields
    Object.keys(responseModel).forEach((responseField) => {
      const value = responseModel[responseField];
      if (value !== undefined && value !== null && value !== "") {
        // Direct assignment for fields that exist in form model
        if ((formModel as any)[responseField] !== undefined && responseField !== "isSigned") {
          (formModel as any)[responseField] = value;
        }
      }
    });
  }
}
